import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Bank } from "@phosphor-icons/react";

/** Client portal banner: offers Direct Debit set-up when the business has GoCardless connected. */
export function DirectDebitBanner() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try { const { data } = await API.get("/client-hub/direct-debit"); setState(data); } catch { setState(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const dd = searchParams.get("dd");
    if (!dd) return;
    if (dd === "ok") toast.success("Thanks — your Direct Debit is set up. Invoices will be collected automatically.");
    else if (dd === "incomplete") toast.warning("The Direct Debit set-up wasn't completed. You can try again any time.");
    else toast.error(searchParams.get("message") || "Direct Debit set-up failed");
    const next = new URLSearchParams(searchParams); next.delete("dd"); next.delete("message");
    setSearchParams(next, { replace: true });
    load();
  }, [searchParams, setSearchParams, load]);

  if (!state?.offered) return null;
  const start = async () => {
    setBusy(true);
    try { const { data } = await API.post("/client-hub/direct-debit/start"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Direct Debit set-up"); setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-md border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-900 px-4 py-3 flex flex-wrap items-center justify-between gap-3 print:hidden" data-testid="portal-direct-debit-banner">
      <div className="flex items-start gap-2 text-xs text-indigo-900 dark:text-indigo-200">
        <Bank size={16} weight="duotone" className="mt-0.5 shrink-0" />
        {state.active
          ? <span data-testid="portal-dd-active">Direct Debit is <b>active</b> — invoices are collected automatically on their due date. Protected by the Direct Debit Guarantee.</span>
          : <span data-testid="portal-dd-offer">Pay invoices automatically by <b>Direct Debit</b> — set up once, never miss a due date. Protected by the Direct Debit Guarantee.</span>}
      </div>
      {!state.active && <Button size="sm" onClick={start} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="portal-dd-setup-btn">{busy ? "Opening…" : "Set up Direct Debit"}</Button>}
    </div>
  );
}
